import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { ALL_QUESTION_IDS, TOTAL_QUESTIONS, RANKS } from "../data/quizzes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ProgressCtx = createContext(null);

function getSessionId() {
  let id = localStorage.getItem("mla_session_id");
  if (!id) {
    id = crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random();
    localStorage.setItem("mla_session_id", id);
  }
  return id;
}

function computeStats(answers) {
  const done = ALL_QUESTION_IDS.filter((id) => answers[id]);
  const answered = done.length;
  const correct = done.filter((id) => answers[id].correct).length;
  const wrong = answered - correct;
  const xp = correct * 10 + wrong * 2;
  const accuracy = answered ? Math.round((correct / answered) * 100) : 0;

  let rankIndex = 0;
  for (let i = 0; i < RANKS.length; i++) if (xp >= RANKS[i].min) rankIndex = i;
  const rank = RANKS[rankIndex];
  const isMax = rankIndex === RANKS.length - 1;
  const nextRank = isMax ? rank : RANKS[rankIndex + 1];
  const span = isMax ? 1 : nextRank.min - rank.min;
  const levelProgress = isMax ? 1 : Math.min(1, (xp - rank.min) / span);

  return { answered, correct, wrong, xp, accuracy, rank, rankIndex, nextRank, isMax, levelProgress };
}

export function ProgressProvider({ children }) {
  const [sessionId] = useState(getSessionId);
  const [answers, setAnswers] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [combo, setCombo] = useState(0);
  const [maxCombo, setMaxCombo] = useState(0);
  const [flash, setFlash] = useState(null);
  const [celebrating, setCelebrating] = useState(false);
  const flashTimer = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/progress/${sessionId}`)
      .then((r) => setAnswers(r.data.answers || {}))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [sessionId]);

  const record = useCallback(
    (questionId, selected, correct) => {
      setAnswers((prev) => {
        if (prev[questionId]) return prev;
        const next = { ...prev, [questionId]: { selected, correct } };
        const answeredCount = ALL_QUESTION_IDS.filter((id) => next[id]).length;
        if (answeredCount === TOTAL_QUESTIONS && !localStorage.getItem("mla_celebrated")) {
          localStorage.setItem("mla_celebrated", "1");
          setTimeout(() => setCelebrating(true), 600);
        }
        return next;
      });

      setCombo((c) => {
        const nc = correct ? c + 1 : 0;
        setMaxCombo((m) => Math.max(m, nc));
        setFlash({ id: questionId, delta: correct ? 10 : 2, correct, combo: nc });
        return nc;
      });

      clearTimeout(flashTimer.current);
      flashTimer.current = setTimeout(() => setFlash(null), 1600);

      axios.put(`${API}/progress/${sessionId}`, { question_id: questionId, selected, correct }).catch(() => {});
    },
    [sessionId]
  );

  const reset = useCallback(() => {
    setAnswers({});
    setCombo(0);
    setMaxCombo(0);
    setFlash(null);
    setCelebrating(false);
    localStorage.removeItem("mla_celebrated");
    axios.delete(`${API}/progress/${sessionId}`).catch(() => {});
  }, [sessionId]);

  const stats = computeStats(answers);

  return (
    <ProgressCtx.Provider
      value={{ answers, record, reset, loaded, combo, maxCombo, flash, stats, celebrating, dismissCelebration: () => setCelebrating(false) }}
    >
      {children}
    </ProgressCtx.Provider>
  );
}

export function useProgress() {
  return useContext(ProgressCtx);
}
